/**
 * The week's chart footer (issue #207, prototype renderWeekCharts, owner 2026-10-27): one chart at a time under
 * the days, above the bottom row. Dekning: each hour against the need; Timer: need and people on shift per hour;
 * Omsetning: the day's labour cost against its sales, with the bottom row's own labour % (MoneyRow dayFigure);
 * Lønn per type: the day's cost split into base pay, each supplement type and what the employer adds on top.
 * One scale for the whole week so the days compare; a click opens the day.
 * ponytail: sales are per day only (no till integration), so Omsetning is per day, not per hour as in Dag.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { T } from '@/i18n';
import type { NeedProfile } from '@/shell/local';
import { num } from '@/ui/format';
import { dayFigure } from './MoneyRow';
import css from './WeekCharts.module.css';
import { burn, costSide, coverState, hourCover, hourRange } from './chartFigures';
import { type SalesDay, hourGaps, needHours } from './staffing';

export type ChartTab = 'cov' | 'hrs' | 'money' | 'kinds';

const kr = (n: number) => `${Math.round(n).toLocaleString('nb-NO')} kr`;
const hh = (h: number) => String(h % 24).padStart(2, '0');
const one = (n: number) => num(Math.round(n * 10) / 10);
// the supplement types take these in turn, the same type the same colour all week
const SUP = ['indigo', 'plum', 'teal', 'blue'];

export function WeekCharts({
  days,
  today,
  need,
  shifts,
  planned,
  cost,
  sales,
  target,
  money,
  tab,
  open,
  onTab,
  onFold,
  onOpenDay,
  dayClass,
  style,
}: {
  days: string[];
  today: string;
  need: NeedProfile;
  shifts: ApiShift[];
  /** the day's planned hours, as the bottom row counts them */
  planned: (d: string) => number;
  cost: Map<string, CostDay>;
  sales: Record<string, SalesDay>;
  target: number;
  /** the reader may see kroner: Omsetning and Lønn per type show */
  money: boolean;
  tab: ChartTab;
  open: boolean;
  onTab: (t: ChartTab) => void;
  onFold: () => void;
  onOpenDay: (d: string) => void;
  dayClass: (d: string) => string | undefined;
  style: React.CSSProperties;
}) {
  // folded, the toggle sits in the bottom row's first cell (owner 2026-10-27: one row at the foot)
  if (!open) return null;
  const tabs: [ChartTab, string][] = [
    ['cov', T('Dekning')],
    ['hrs', T('Timer')],
    ...(money
      ? ([
          ['money', T('Omsetning')],
          ['kinds', T('Lønn per type')],
        ] as [ChartTab, string][])
      : []),
  ];
  const t = tabs.some(([k]) => k === tab) ? tab : 'cov';
  const [h0, h1] = hourRange(need, days, shifts);
  const cover = new Map(days.map((d) => [d, hourCover(need, d, shifts, h0, h1)]));
  const top = Math.max(4, ...[...cover.values()].flat().map((z) => Math.max(z.need, z.on)));
  const fig = new Map(days.map((d) => [d, dayFigure(cost.get(d), sales[d], d < today)]));
  const smax = Math.max(1, ...[...fig.values()].map((f) => Math.max(f.sales ?? 0, f.cost)));
  const side = new Map(days.map((d) => [d, costSide(cost.get(d), d < today)]));
  const kmax = Math.max(1, ...[...side.values()].map((s) => s?.cost ?? 0));
  const supNames = [
    ...new Set([...side.values()].flatMap((s) => s?.parts?.supplements.map((x) => x.name) ?? [])),
  ];
  const supTone = (n: string) => SUP[supNames.indexOf(n) % SUP.length];

  const W = 100;
  const H = 56;
  const x = (h: number) => ((h - h0) / (h1 - h0)) * W;
  const y = (v: number) => H - (v / top) * (H - 4);

  const cell = (d: string) => {
    const hs = cover.get(d) ?? [];
    if (t === 'cov') {
      const short = hs.filter((z) => z.low < z.need);
      return {
        title: short.length
          ? `${T('Mangler folk kl')} ${short.map((z) => hh(z.h)).join(', ')}`
          : T('Dekket hver time'),
        body: (
          <>
            <div className={css.cov} style={{ gridTemplateColumns: `repeat(${h1 - h0}, 1fr)` }}>
              {hs.map((z) => (
                <i
                  key={z.h}
                  className={css[coverState(z)]}
                  title={`kl ${hh(z.h)}: ${one(z.on)} ${T('på jobb')}, ${T('behov')} ${num(z.need)}`}
                  data-state={coverState(z)}
                />
              ))}
            </div>
            <span className={css.ax}>
              <span>{hh(h0)}</span>
              <span>{hh(Math.round((h0 + h1 - 1) / 2))}</span>
              <span>{hh(h1 - 1)}</span>
            </span>
          </>
        ),
      };
    }
    if (t === 'hrs') {
      const nh = needHours(need, d);
      const p = planned(d);
      const gaps = nh == null ? [] : hourGaps(need, d, shifts);
      const off = nh != null && (p + 0.05 < nh || gaps.length > 0);
      const step = (f: (z: (typeof hs)[number]) => number) =>
        hs
          .map(
            (z, k) => `${k ? 'L' : 'M'}${x(z.h).toFixed(2)} ${y(f(z)).toFixed(2)}H${x(z.h + 1).toFixed(2)}`,
          )
          .join('');
      const short = hs.filter((z) => z.on + 0.01 < z.need);
      const fig = nh == null ? `${one(p)} t` : `${one(p)} ${T('av')} ${num(nh)} t`;
      return {
        title: short.length ? `${fig}, ${T('mangler folk kl')} ${short.map((z) => hh(z.h)).join(', ')}` : fig,
        body: (
          <>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={css.svg} aria-hidden="true">
              {short.map((z) => (
                <rect
                  key={z.h}
                  x={x(z.h)}
                  y={y(z.need)}
                  width={x(z.h + 1) - x(z.h)}
                  height={y(z.on) - y(z.need)}
                  className={css.short}
                />
              ))}
              {hs.length > 0 && (
                <>
                  <path d={`${step((z) => z.need)}V${H}H${x(h0)}Z`} className={css.need} />
                  <path d={step((z) => z.on)} className={css.on} />
                </>
              )}
            </svg>
            <span className={`${css.f} ${off ? css.off : ''}`}>{fig}</span>
          </>
        ),
      };
    }
    if (t === 'money') {
      const f = fig.get(d) ?? dayFigure(undefined, undefined, false);
      const over = f.share != null && f.share > target;
      const fc = d >= today;
      return {
        title: `${T('Lønnskost')} ${kr(f.cost)}${f.sales ? `, ${fc ? T('prognose') : T('omsetning')} ${kr(f.sales)}` : ''}`,
        body: (
          <>
            <div className={css.bars}>
              <i
                className={`${css.sales} ${fc ? css.fc : ''}`}
                style={{ height: `${((f.sales ?? 0) / smax) * 100}%` }}
              />
              <i className={css.cost} style={{ height: `${(f.cost / smax) * 100}%` }} />
            </div>
            <span className={`${css.f} ${over ? css.off : ''}`} data-testid="chart-pct">
              {f.share == null ? '–' : `${one(f.share)} %`}
            </span>
          </>
        ),
      };
    }
    const s = side.get(d);
    const parts = s?.parts;
    const items: [string, number, string][] = parts
      ? [
          [T('Grunnlønn'), parts.base, css.base ?? ''],
          ...parts.supplements.map(
            (x) =>
              [x.name, x.cost, `${css.sup} ${css[supTone(x.name) ?? 'indigo']}`] as [string, number, string],
          ),
          [T('Feriepenger, pensjon og AGA'), parts.on_top, css.top ?? ''],
        ]
      : [];
    return {
      title: items.map(([n, v]) => `${n} ${kr(v)}`).join(', '),
      body: (
        <>
          <div
            className={css.stk}
            style={{ height: `${Math.max(1, Math.round(((s?.cost ?? 0) / kmax) * 54))}px` }}
          >
            {items.map(([n, v, c]) => (
              <i key={n} className={c} style={{ flex: v }} />
            ))}
          </div>
          <span className={css.f}>{kr(s?.cost ?? 0)}</span>
        </>
      ),
    };
  };

  const key = {
    cov: (
      <>
        {(
          [
            ['short', T('Mangler 1+')],
            ['thin', T('Litt få')],
            ['ok', T('Nok')],
            ['over', T('For mange')],
          ] as const
        ).map(([k, n]) => (
          <span key={k}>
            <i className={`${css.k} ${css.ck} ${css[k]}`} />
            {n}
          </span>
        ))}
      </>
    ),
    hrs: (
      <>
        <span>
          <i className={`${css.k} ${css.kneed}`} />
          {T('Behov')}
        </span>
        <span>
          <i className={`${css.k} ${css.kon}`} />
          {T('På jobb')}
        </span>
        <span>
          <i className={`${css.k} ${css.kshort}`} />
          {T('Mangler folk')}
        </span>
      </>
    ),
    money: (
      <>
        <span>
          <i className={`${css.k} ${css.sales}`} />
          {T('Omsetning')}
        </span>
        <span>
          <i className={`${css.k} ${css.fc}`} />
          {T('Prognose')}
        </span>
        <span>
          <i className={`${css.k} ${css.cost}`} />
          {T('Lønnskost')}
        </span>
      </>
    ),
    kinds: (
      <>
        <span>
          <i className={`${css.k} ${css.base}`} />
          {T('Grunnlønn')}
        </span>
        {supNames.map((n) => (
          <span key={n}>
            <i className={`${css.k} ${css[supTone(n) ?? 'indigo']}`} />
            {n}
          </span>
        ))}
        <span>
          <i className={`${css.k} ${css.top}`} />
          {T('Feriepenger, pensjon og AGA')}
        </span>
      </>
    ),
  }[t];

  return (
    <div className={`grow ${css.row}`} style={style} data-testid="week-charts">
      <div className={css.key}>
        {/* one picker and «Skjul» on one line, not four tabs wrapping to three (owner 2026-10-27) */}
        <div className={css.tabs}>
          <select
            className={`inp ${css.tabPick}`}
            aria-label={T('Diagram')}
            value={t}
            onChange={(e) => onTab(e.target.value as ChartTab)}
          >
            {tabs.map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
          <button type="button" className={css.fold} onClick={onFold}>
            {T('Skjul')}
          </button>
        </div>
        <div className={css.legend}>{key}</div>
      </div>
      {days.map((d) => {
        const c = cell(d);
        return (
          <div key={d} className={`${css.day} ${dayClass(d) ?? ''}`}>
            <button
              type="button"
              className={css.cell}
              title={c.title}
              aria-label={`${new Date(`${d}T12:00:00Z`).toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })}: ${c.title}`}
              onClick={() => onOpenDay(d)}
              data-testid="chart-day"
            >
              {c.body}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * «Lønn mot budsjett» over the month view (#207, prototype burnHTML): the labour cost added up day by day, clocked
 * up to yesterday and planned from today, so the line's end is the forecast for the month; the month's budget
 * from PowerOffice Go as a line when there is one.
 */
export function MonthBurn({
  days,
  today,
  cost,
  budget,
  open,
  onFold,
}: {
  days: string[];
  today: string;
  cost: Map<string, CostDay>;
  budget: number | null;
  open: boolean;
  onFold: () => void;
}) {
  if (!open)
    return (
      <div className={css.burnOff} data-testid="month-burn">
        <button type="button" className={css.fold} onClick={onFold}>
          {T('Vis lønn mot budsjett')}
        </button>
      </div>
    );
  const b = burn(days, today, cost);
  const W = 1000;
  const H = 150;
  const n = days.length;
  const max = Math.max(1, budget ?? 0, b.forecast) * 1.05;
  const x = (i: number) => (n > 1 ? (i / (n - 1)) * W : 0);
  const y = (v: number) => H - (v / max) * (H - 8);
  const path = (from: number, to: number) =>
    b.cum
      .slice(from, to)
      .map((v, i) => `${i ? 'L' : 'M'}${x(i + from).toFixed(1)} ${y(v).toFixed(1)}`)
      .join('');
  const over = budget != null && b.forecast > budget;
  const month = new Date(`${days[0] ?? today}T12:00:00Z`).toLocaleDateString('nb-NO', { month: 'long' });
  return (
    <div className={css.burn} data-testid="month-burn">
      <div className={css.bkey}>
        <b>{T('Lønn mot budsjett, {m}', { m: month })}</b>
        <button type="button" className={css.fold} onClick={onFold}>
          {T('Skjul')}
        </button>
        <div className={css.legend}>
          <span>
            <i className={`${css.k} ${css.kon}`} />
            {T('Lønnskost hittil')}
          </span>
          <span>
            <i className={`${css.k} ${css.kfc}`} />
            {T('Prognose til månedsslutt')}
          </span>
          {budget != null && (
            <span>
              <i className={`${css.k} ${css.kbud}`} />
              {T('Budsjett')}
            </span>
          )}
        </div>
        <p className={css.f}>
          {kr(b.sofar)} {T('hittil')} · {T('prognose')}{' '}
          <b className={over ? css.bover : undefined} data-testid="burn-forecast">
            {budget != null ? `${kr(b.forecast)} ${T('av')} ${kr(budget)}` : kr(b.forecast)}
          </b>
        </p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={css.bsvg} aria-hidden="true">
        {budget != null && <line x1={0} x2={W} y1={y(budget)} y2={y(budget)} className={css.bud} />}
        {b.done > 0 && <path d={path(0, b.done)} className={css.on} />}
        <path d={path(Math.max(0, b.done - 1), n)} className={css.fcl} />
        {b.done > 0 && b.done < n && <line x1={x(b.done)} x2={x(b.done)} y1={0} y2={H} className={css.now} />}
      </svg>
    </div>
  );
}
